import { GIFEncoder, quantize, applyPalette } from 'gifenc';
import { DEFAULT_TRANSPARENCY_TOLERANCE } from '../constants/export_constants';
import { parseTransparencyColor } from './TransparencyColor';

export const getGifBuilder = (
  width, height, transparentColor, keepPreviousFrames = false, repeat = 0,
  transparencyTolerance = DEFAULT_TRANSPARENCY_TOLERANCE,
) => {
  const encoder = new GIFEncoder();
  let pendingFrame = null;
  const colorKey = parseTransparencyColor(transparentColor);
  // Percentage of the maximum RGB distance. Zero removes exact matches only.
  const maxColorDistanceSquared = 3 * (255 * transparencyTolerance / 100) ** 2;

  const writePendingFrame = (dispose) => {
    if (!pendingFrame) return;
    const { index, ...options } = pendingFrame;
    encoder.writeFrame(index, width, height, { ...options, dispose, repeat });
    pendingFrame = null;
  };

  const listeners = {};

  const on = (event, callback) => {
    listeners[event] = callback;
  };

  const addFrame = (input, options = {}) => {
    const { delay } = options;
    let data;

    if (input.data && input.width && input.height && input.data.length) {
      data = input.data;
    } else if (input.getContext) {
      data = input.getContext('2d').getImageData(0, 0, width, height).data;
    } else {
      // Fallback: try to assume it's a context or canvas-like
      try {
        data = input.getImageData(0, 0, width, height).data;
      } catch (e) {
        try {
          data = input.getContext('2d').getImageData(0, 0, width, height).data;
        } catch (e2) {
          console.error('GifBuilder: Invalid input for addFrame', input);
          return;
        }
      }
    }

    let palette;
    let index;
    let transparentIndex = -1;

    // Classify original pixels before quantization so background shades cannot
    // survive as separate palette colors or share transparency with foreground.
    const transparentPixels = [];
    const opaquePixels = []; // flattened RGBA

    for (let i = 0; i < data.length; i += 4) {
      const matchesKey = colorKey && (
        (data[i] - colorKey[0]) ** 2 +
        (data[i + 1] - colorKey[1]) ** 2 +
        (data[i + 2] - colorKey[2]) ** 2
      ) <= maxColorDistanceSquared;
      if (data[i + 3] < 128 || matchesKey) {
        transparentPixels.push(i / 4);
      } else {
        // Push RGBA (Alpha 255)
        opaquePixels.push(data[i], data[i + 1], data[i + 2], 255);
      }
    }

    if (transparentPixels.length > 0) {
      // Has transparency.
      const opaqueData = new Uint8Array(opaquePixels);

      // Handle case where NO opaque pixels (fully transparent frame)
      if (opaqueData.length === 0) {
        palette = [[0, 0, 0]]; // Dummy palette
        index = new Uint8Array(width * height).fill(0);
        transparentIndex = 0;
      } else {
        // Quantize opaque pixels to 255 colors
        // Default format is rgba, which works better with Uint32Array views often used in quantizers
        palette = quantize(opaqueData, 255);

        // Apply palette to ALL pixels *before* adding the transparent color.
        // This ensures opaque pixels map to the quantized colors (0-254) and NOT to the reserved transparent slot.
        // Transparent pixels will map to *some* color in the palette temporarily.
        index = applyPalette(data, palette);

        // Add dummy transparent color at the end
        // We use [0,0,0] but it doesn't matter as it will be transparent and no opaque pixel points to it
        palette.push([0, 0, 0]);
        transparentIndex = palette.length - 1;

        // Force indices of transparent pixels to transparentIndex
        for (const pIndex of transparentPixels) {
          index[pIndex] = transparentIndex;
        }
      }
    } else {
      // No transparency detected
      palette = quantize(data, 256);
      index = applyPalette(data, palette);
    }

    // Keep one quantized frame pending so render() can mark the final frame
    // for disposal without requiring callers to know which frame is last.
    writePendingFrame(keepPreviousFrames ? 1 : 2);
    pendingFrame = {
      index,
      palette,
      delay,
      transparent: transparentIndex !== -1,
      transparentIndex,
    };
  };

  const render = () => {
    // Retain pixels only within a loop. Always clear the full final frame
    // before frame zero so transparent areas cannot reveal the previous loop.
    writePendingFrame(repeat === -1 ? 1 : 2);
    encoder.finish();
    const buffer = encoder.bytes();
    const blob = new Blob([buffer], { type: 'image/gif' });
    if (listeners['finished']) {
      listeners['finished'](blob);
    }
  };

  return {
    addFrame,
    on,
    render,
  };
};
